<template lang="pug">
  .main-container
    krea-create-in-team-modal(v-if='showCreateInTeamModal')
    .card-container.width-100.content.row.middle
      .item-2.m-right-5.row.middle.center
        img(:src='illustration' width='350px')
      .item-3
        section.setting(v-if='!setNewContentType')
          h2.title-medium.m-bottom-2
            | {{ setNewLanguage ? $t("krea.createContent.chooseAnotherContentLanguage") : $t("krea.createContent.chooseContentLanguage") }}
          k-select.select.m-bottom-4(
            @selectOption='updateLanguage'
            :options='languagesOptions'
            withBorder
            name='locale'
            keyName='value'
            labelName='label'
            :placeholder='$t("krea.createContent.chooseALanguage")'
          )
        section.setting(v-if='!setNewLanguage')
          h2.title-medium.m-bottom-2 {{ setNewContentType ? $t("krea.createContent.chooseNewContentType") : $t("krea.createContent.chooseContentType") }}
          .row.middle.selectors.m-bottom-2.width-100
            .selector.item-1.row.m-right-1(
              @click='changeContentType(contentType.project)'
              :class='{selected: contentSelected === contentType.project}'
            )
              checkbox.m-bottom-auto(circle :checked='contentSelected === contentType.project')
              .item-1.m-left-2.m-right-1
                h2.title-small.m-bottom-05 {{ $t("krea.createContent.project") }}
                p.text-normal.dark-gray {{ $t("krea.createContent.projectDesc") }}
            .selector.item-1.row.m-left-1(
              @click='changeContentType(contentType.program)'
              :class='{selected: contentSelected === contentType.program}'
            )
              checkbox.m-bottom-auto(circle :checked='contentSelected === contentType.program')
              .item-1.m-left-2.m-right-1
                h2.title-small.m-bottom-05 {{ $t("krea.createContent.educationalProgram") }}
                p.text-normal.dark-gray {{ $t("krea.createContent.educationalProgramDesc") }}
        section.setting.m-bottom-4(v-if='!setNewLanguage')
          h2.title-medium.m-bottom-2 {{ $t("krea.createContent.createInTeam") }}
          p.text-normal.dark-gray {{ $t("krea.createContent.createDescription") }}
          button.invite-button.row.middle.width-100.m-top-1(@click='showInviteModal')
            simple-svg.m-right-2.blue-icon(:filepath='addUserIcon' width='22px' height='22px')
            span.title-small {{ $t("krea.createContent.invite") }}
            .invited-users.row.middle.m-left-auto(v-if='invitedUsers.length')
              img.avatar(v-for='user of invitedUsers.slice(0, 4)' :src='user.avatar || defaultProfileImage')
              .more-users.row.middle.center(v-if='invitedUsers.length > 4') {{ '+' + (invitedUsers.length - 4) }}
        .row.middle(v-if='showError')
          simple-svg.m-right-1.red-stroke-icon(:filepath='errorIcon' width='18px' height='18px')
          h2.text-normal.red {{ errorMessage }}
        .row.space-between.middle.m-top-2
          a.guide.secondary-button.with-icon(target='_blank' rel='noreferrer noopener' :href="kreaContentGuide")
            simple-svg.m-right-1(:filepath='downloadIcon' width='18px' height='18px')
            | {{ $t("krea.createContent.downloadGuide") }}
          button.primary-button.submit(@click='submit') {{ $t("krea.createContent.save") }}
</template>

<script>
import illustration from '@/assets/il_language_selection.svg'
import checkIcon from '@/assets/ic_check.svg'
import KSelect from '@/components/KSelect'
import Checkbox from '@/components/Inputs/Checkbox'
import { contentType } from '../utils/constants'
import { allLanguages } from '../constants/languages'
import { mapActions, mapState } from 'vuex'
import { routes } from '../router'
import errorIcon from '@/assets/ic_stop.svg'
import crossIcon from '@/assets/ic_cross.svg'
import downloadIcon from '@/assets/ic_download.svg'
import { required } from 'vuelidate/lib/validators'
import { i18n } from '../main'
import userRole from '../mixins/userRole'
import addUserIcon from '@/assets/ic_user_add.svg'
import KreaCreateInTeamModal from '../components/Modals/KreaCreateInTeamModal'
import defaultProfileImage from '@/assets/empty_profile.svg'

export default {
  mixins: [userRole],
  components: { KSelect, Checkbox, KreaCreateInTeamModal },
  data() {
    return {
      defaultProfileImage,
      addUserIcon,
      downloadIcon,
      errorIcon,
      crossIcon,
      illustration,
      contentType,
      checkIcon,
      showError: false,
      contentSelected: null,
      invitedUsers: [],
      languageSelected: null
    }
  },
  computed: {
    ...mapState({
      content: state => state.kreaEducationalContent.content,
      locale: state => state.behavior.locale,
      customFields: state => state.userInfo.customFields,
      showCreateInTeamModal: state => state.behavior.kreaCreateInTeamModal.show
    }),
    kreaContentGuide() {
      return process.env[`VUE_APP_KREA_CREATE_CONTENT_GUIDE_${this.locale}`]
    },
    contentLanguages() {
      return this.content && Object.keys(this.content.activatedLanguages || {})
    },
    languagesOptions() {
      if (!this.setNewLanguage) {
        return allLanguages
          .filter(lang =>
            this.hasWhiteLabel && this.customFields
              ? this.customFields.customizedLanguages.includes(lang.code) || lang.default
              : lang.default
          )
          .map(item => ({
            label: `<div class='row middle'><img class='m4-right-2' src='${item.icon}'/><p class='text-normal'>${item.label}</p></div>`,
            value: item.lang
          }))
      } else {
        return allLanguages
          .filter(lang =>
            this.hasWhiteLabel && this.customFields
              ? this.customFields.customizedLanguages.includes(lang.code) || lang.default
              : lang.default
          )
          .map(item => ({
            label: `<div class='row middle'><img class='m4-right-2' src='${item.icon}'/><p class='text-normal'>${item.label}</p></div>`,
            value: item.lang
          }))
          .filter(item => this.contentLanguages && !this.contentLanguages.includes(item.value))
      }
    },
    setNewLanguage() {
      return this.$route.query.setting === 'language'
    },
    setNewContentType() {
      return this.$route.query.setting === 'contentType'
    },
    contentId() {
      return this.$route.query.contentId
    },
    errorMessage() {
      if (this.$v.contentSelected.$invalid && this.$v.languageSelected.$invalid) {
        return i18n.t('krea.createContent.errors.contentAndLanguageError')
      } else if (this.$v.contentSelected.$invalid) return i18n.t('krea.createContent.errors.contentError')
      else return i18n.t('krea.createContent.errors.languageError')
    }
  },
  async created() {
    this.showElement({ element: 'fullPageLoader' })
    await this.getUserCustomFields()
    if (this.contentId) await this.hydrateKreaEducationalContent(this.contentId)
    if (this.setNewContentType) {
      this.contentSelected = this.content.contentType
    }
    this.hideElement({ element: 'fullPageLoader' })
  },
  methods: {
    ...mapActions([
      'createKreaEducationalContent',
      'editKreaEducationalContentType',
      'addLanguageKreaEducationalContent',
      'hydrateKreaEducationalContent',
      'associateAuthorsToKreaContent',
      'getUserCustomFields',
      'showElement',
      'hideElement'
    ]),
    showInviteModal() {
      this.showElement({
        element: 'kreaCreateInTeamModal',
        props: {
          selectedUsers: this.invitedUsers,
          onSubmit: this.addInvitedUsers
        }
      })
    },
    addInvitedUsers(users) {
      this.invitedUsers = users
      this.showElement({
        element: 'successModal',
        props: { title: i18n.t('krea.createContent.invitedSuccess') }
      })
    },
    changeContentType(value) {
      this.showError = false
      this.contentSelected = value
    },
    updateLanguage(name, value) {
      this.showError = false
      this.languageSelected = value
    },
    submit() {
      if (this.setNewLanguage) return this.addNewLanguage()
      else if (this.setNewContentType) return this.editNewContentType()
      else return this.createContent()
    },
    async createContent() {
      this.showError = false
      this.$v.$touch()
      if (this.$v.$error) {
        this.showError = true
        return
      }
      this.showElement({ element: 'fullPageLoader' })
      const { success, id } = await this.createKreaEducationalContent({ contentType: this.contentSelected, locale: this.languageSelected })
      if (success) {
        if (this.invitedUsers.length) {
          await this.associateAuthorsToKreaContent({
            authors: this.invitedUsers.map(author => ({ authorId: author.id, permission: author.permission })),
            id,
            url: window.location.origin + `/krea/content/${id}`
          })
        }
        this.$router.push({ name: routes.kreaEducationalContent, params: { id } })
      }
      this.$router.push({ name: routes.kreaEducationalContent, params: { id } })
      this.hideElement({ element: 'fullPageLoader' })
    },
    async addNewLanguage() {
      this.showError = false
      this.$v.$touch()
      if (this.$v.$error) {
        this.showError = true
        return
      }
      this.showElement({ element: 'fullPageLoader' })
      await this.addLanguageKreaEducationalContent({ id: this.contentId, locale: this.languageSelected })
      this.$router.push({ name: routes.kreaEducationalContent, params: { id: this.contentId }, query: { locale: this.languageSelected } })
      this.hideElement({ element: 'fullPageLoader' })
    },
    async editNewContentType() {
      this.showError = false
      this.$v.$touch()
      if (this.$v.$error) {
        this.showError = true
        return
      }
      this.showElement({ element: 'fullPageLoader' })
      await this.editKreaEducationalContentType({ contentType: this.contentSelected, id: this.contentId })
      this.$router.push({ name: routes.kreaEducationalContent, params: { id: this.contentId } })
      this.hideElement({ element: 'fullPageLoader' })
    }
  },
  validations() {
    if (this.setNewLanguage) return ({ languageSelected: { required } })
    else if (this.setNewContentType) return ({ contentSelected: { required } })
    else {
      return {
        contentSelected: { required },
        languageSelected: { required }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.guide{
  max-width: none;
}

.invite-button{
  padding: 16px;
  border-radius: 4px;
  box-shadow: 0px 2px 2px 0px #73798540;
  .invited-users{
    .avatar{
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 1px solid $white;
      margin-left: -10px;
    }
    .more-users{
      margin-left: -10px;
      background: $white;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      color: $dark-gray;
      border: 1px solid $dark-gray;
    }
  }
}

.content{
  flex-direction: row;
  max-width: none;
  padding: 50px;
  margin: 0;
  margin: 45px 0;
  min-height: 520px;
}

.submit{
  max-width: none;
}

.select{
  max-width: 250px;
}

.selectors{
  align-items: stretch;
}

.selector{
  border-radius: 10px;
  box-shadow: 0px 2px 2px 0px #73798540;
  flex-direction: row;
  min-height: 140px;
  max-width: none;
  padding: 20px;
  border: 2px solid $white;
  cursor: pointer;
  .checkbox{
    width: 20px;
    height: 20px;
    border: 2px solid $blue;
    border-radius: 50%;
  }
  &.selected{
    border: 2px solid $blue;
  }
}
</style>
